import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, Home } from 'lucide-react';
import { toast } from 'sonner';
import { PWAInstallPrompt } from '../components/PWAInstallPrompt';

export const LoginPage = () => {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error('Inserisci username e password');
      return;
    }
    
    setLoading(true);
    try {
      await login(username, password);
      toast.success('Accesso effettuato');
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Credenziali non valide');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden bg-gradient-to-br from-slate-100 to-blue-50">
      {/* Header con pulsante Home - BEN VISIBILE su mobile/tablet */}
      <header className="w-full p-4 sm:p-5 flex items-center justify-between z-20">
        <Link to="/" className="flex items-center gap-2 group">
          <div className="p-2 sm:p-2.5 bg-white rounded-xl shadow-md group-hover:shadow-lg transition-shadow">
            <Home className="w-5 h-5 sm:w-6 sm:h-6 text-blue-700" />
          </div>
          <span className="text-sm sm:text-base font-medium text-slate-700 group-hover:text-blue-700 transition-colors">
            Torna al sito
          </span>
        </Link>
      </header>

      {/* Logo pattern background */}
      <div 
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: 'url(/logo-servicare.png)',
          backgroundSize: '180px',
          backgroundRepeat: 'repeat',
        }}
      />
      
      {/* Decorative gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/5 via-transparent to-orange-500/5" />
      
      {/* Main content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md relative z-10 shadow-2xl border-0 bg-white/95 backdrop-blur-sm" data-testid="login-card">
          <CardHeader className="text-center pb-0 pt-4">
            {/* Complete logo */}
            <div className="mx-auto">
              <img 
                src="/logo-complete.png" 
                alt="ServiCare" 
                className="h-36 sm:h-40 object-contain mx-auto"
                loading="eager"
              />
            </div>
            <CardDescription className="text-slate-500 mt-0">
              Gestione Pasti per Strutture Assistenziali
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-slate-700">Username</Label>
                <Input
                  id="username"
                  data-testid="login-username"
                  type="text"
                  placeholder="Inserisci username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="h-11 border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                  autoComplete="username"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700">Password</Label>
                <Input
                  id="password"
                  data-testid="login-password"
                  type="password"
                  placeholder="Inserisci password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                  autoComplete="current-password"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full h-11 bg-gradient-to-r from-blue-800 to-blue-700 hover:from-blue-900 hover:to-blue-800 text-white font-medium shadow-lg"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Accesso in corso...
                  </>
                ) : (
                  'Accedi'
                )}
              </Button>

              <div className="text-center">
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-blue-700 hover:text-blue-800 hover:underline"
                  data-testid="forgot-password-link"
                >
                  Password dimenticata?
                </Link>
              </div>
            </form>
            
            <p className="text-center text-sm text-slate-500 mt-4">
              Contatta l'amministratore per le credenziali
            </p>
            
            {/* PWA Install Prompt */}
            <div className="mt-6">
              <PWAInstallPrompt variant="banner" />
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Footer */}
      <footer className="w-full p-4 text-center z-10">
        <p className="text-xs text-slate-400">
          © 2025 ServiCare - Tutti i diritti riservati
        </p>
      </footer>
    </div>
  );
};
